(ns rothko.editor.component
  (:require ["react" :refer [createRef]]
            [reagent.core :as r]
            [rothko.dom.event :as event]
            [rothko.dom.input :as input]
            [rothko.dom.selection :as selection]
            [rothko.editor.actions :as actions]
            [rothko.editor.document.component :as document]
            [rothko.editor.document.script :as script]))

(defn action-handler
  [event->action !state event]
  (let [{:keys [implementation]} @!state
        action (-> event
                   event->action
                   (assoc ::actions/implementation implementation))]
    (when-let [transformation (actions/->transformation action)]
      (event/stop event)
      (swap! !state update :render not)
      (actions/enqueue-transformation! transformation)
      (actions/log-action! action))))

(def on-key-down-handler (partial action-handler #(-> % input/event->key actions/key)))
(def on-click-handler (partial action-handler (constantly actions/click)))
(def on-double-click-handler (partial action-handler (constantly actions/double-click)))

(defn on-blur-handler
  [!document on-blur event]
  (event/stop event)
  (when on-blur
    (on-blur (:script @!document))))

(defn ro-dom-editor
  "The read-only editor of the document being annotated, consisting of the Original and Corrected views.
   This view can be hidden/shown by the annotator and the intention is that it responds to mouse and keyboard events,
   but within the R/W Annotated view."
  [{:keys [!document !state appearance init ro-view-toolbar] :as props}]
  (let [ref (createRef)
        selection-change-handler (fn []
                                   (when-let [offsets (selection/get-offsets-relative-to (.-current ref))]
                                     (swap! !state assoc :ro-document-offsets offsets)))
        editor-dom-element (atom nil)]
    (r/create-class
     {:component-did-mount
      (fn [_] (when init (init (:script @!document)))
        (.addEventListener js/document "selectionchange" selection-change-handler))

      :component-will-unmount
      (fn [_] (.removeEventListener js/document "selectionchange" selection-change-handler))

      :reagent-render
      (fn [_]
        [:div.editor
         (cond-> {:class (conj appearance :ro-editor)}
           (:ro-view-hidden @!state) (assoc :style {:display :none}))
         ro-view-toolbar
         [:div.editor__content
          {:ref
           (fn [element]
             (if element
               (.addEventListener element "beforeinput" event/stop)
               (.removeEventListener @editor-dom-element "beforeinput" event/stop))
             (reset! editor-dom-element element))
           :spellCheck false
           :tab-index 1
           :on-cut event/stop, :on-paste event/stop, :on-drop event/stop, :on-blur event/stop
           :on-key-down #(on-key-down-handler !state %)
           #_#_:on-click #(on-click-handler !state %)
           #_#_:on-double-click #(on- double-click-handler !state %)}

          [document/ro-document (assoc props :ref ref)]]])})))

(defn dom-editor
  [{:keys [!state appearance !document init on-blur toolbar editflag-fn on-context-menu
           skip-corrected-token?-fn skip-original-token?-fn skip-succeeding-space?]
    :as props}]
  ;; TODO rothko#239
  (assert (-> @!state :implementation nil? not))
  (let [ref (createRef)
        skip-annotated-token?-fn (constantly false)

        skiping? (fn [offsets]
                   (if offsets
                     (case (:view @!state) :original skip-original-token?-fn :corrected skip-corrected-token?-fn skip-annotated-token?-fn)
                     (case (:ro-view @!state) :ro-original skip-original-token?-fn :ro-corrected skip-corrected-token?-fn)))

        !render-interlock (atom false)
        !previous-selection (atom nil)

        after-render
        (fn []
          (let [skip?-fn (case (:view @!state) :original skip-original-token?-fn :corrected skip-corrected-token?-fn skip-annotated-token?-fn)
                offs (script/selection->document-offsets @!document skip?-fn skip-succeeding-space?)]
            (reset! !render-interlock true)
            (selection/set (.-current ref) offs)))

        selection-change-handler
        (fn self []
          (if @!render-interlock
            (reset! !render-interlock false)
            (let [active-element (-> js/document (.-activeElement))
                  active-editor? (loop [el active-element]
                                   (cond
                                     (nil? el) false
                                     (.contains (.-classList el) "editor") true
                                     :else (recur (.-parentElement el))))
                  rw-offsets (selection/get-offsets-relative-to (.-current ref))
                  document-offsets (or rw-offsets (:ro-document-offsets @!state))
                  sel (script/document-offsets->selection
                       (get @!document :script)
                       document-offsets
                       (skiping? rw-offsets)
                       skip-succeeding-space?)]
              (when active-editor?
                (if document-offsets
                  (do (reset! !previous-selection (get @!document :selection))
                      (swap! !document assoc :selection sel))
                  (after-render))))))

        editor-dom-element (atom nil)]

    (r/create-class
     {:component-did-mount
      (fn [_]
        (when init (init (:script @!document)))
        (js/setTimeout #(when @editor-dom-element (.focus @editor-dom-element)) 0)
        (.addEventListener js/document "selectionchange" selection-change-handler))

      :component-will-unmount
      (fn [_]
        (.removeEventListener js/document "selectionchange" selection-change-handler))

      :reagent-render
      (fn [{:keys [spelling-dictionary]}]
        [:div.editor
         {:class (conj appearance :main-editor)}
         toolbar
         [:div.editor__content
          (let [editflag-fn (or editflag-fn (constantly true))]
            ;; Registering a native event listener rather than using the react synthentic event as it
            ;; does not trigger on deletions which we would also like to prevent.
            ;; https://github.com/facebook/react/issues/11211

            ;; Shame react doesn't pass reference to the dom element when being unmounted, only null
            ;; otherwise we wouldn't have to store reference to it in an atom.
            {:ref (fn [element]
                    (if element (.addEventListener element "beforeinput" event/stop) (.removeEventListener @editor-dom-element "beforeinput" event/stop))
                    (reset! editor-dom-element element))
             :contentEditable (editflag-fn :can-interact?)
             :tab-index 0
             :suppressContentEditableWarning true
             :spellCheck false
             :on-cut event/stop
             :on-paste event/stop
             :on-drop event/stop
             :on-blur #(on-blur-handler !document on-blur %)
             :on-click (fn [_] (when (and (not= (@!state :view) :annotated)
                                          (editflag-fn :click-to-anno?))
                                 (swap! !state assoc :view :annotated)))
             :on-key-down (fn [e]
                            (if (editflag-fn :can-type?)
                              (on-key-down-handler !state e)
                              (when (editflag-fn :type-to-anno?)
                                (swap! !state assoc :view :annotated))))

             ;; Override the default context menu functionality (triggered by ctrl-click or right-click) in the transcription views to set
             ;; the wave-form cursor to the time corresponding to the selected token.
             :on-context-menu (fn [e]
                                (when on-context-menu
                                  (event/stop e)
                                  (when-let [document-offsets (selection/get-offsets-relative-to (.-current ref))]
                                    (on-context-menu (swap! !document
                                                            assoc
                                                            :selection (script/document-offsets->selection (:script @!document) document-offsets))))))

             :on-double-click (fn [e]
                                (let [{prev-index :start-index :as prev} @!previous-selection
                                      {index :start-index} (get @!document :selection)]
                                  (when (not= prev-index index)
                                    (swap! !document assoc :selection prev)))
                                (on-double-click-handler !state e))})
          [document/document
           (assoc props
                  :spelling-dictionary spelling-dictionary
                  :after-render after-render
                  :ref ref)]

          ;; These are useful for debugging sometimes
          #_[:pre (with-out-str (cljs.pprint/pprint @!state))]
          #_[:pre (with-out-str (cljs.pprint/pprint @!document))]]

         ;; This must go outside the 'div.editor_content' element above to
         ;; prevent the RO content being taken into consideration when getting
         ;; relative offsets (in
         ;; `rothko.dom.selection/get-offsets-relative-to`).
         [ro-dom-editor props]])})))
